//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { addToCollections } from '@apis/bulkActionsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

import { clearItems } from 'app/bulk-actions/bulk-actions-actions/action-creators';
import { getBulkActionsSelectedItems } from 'app/bulk-actions/selectors/bulk-actions-selector';

interface AddToStreamVariables {
  collectionIds: string[];
  cuids: PartialCuid[];
}

export const useBulkAddToStreamMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const selectedItems = useSelector(getBulkActionsSelectedItems);

  const mutationResult = useMutation({
    mutationFn: async ({ collectionIds, cuids }: AddToStreamVariables) => {
      const response = await addToCollections(cuids, collectionIds);
      return {
        count: response.cuids.length,
        streamsCount: collectionIds.length,
      };
    },
    onSuccess: ({ count, streamsCount }) => {
      const streamLabel = streamsCount === 1 ? 'Stream' : 'Streams';
      dispatch(clearItems());
      dispatch(
        toastNotificationsActions.addToastNotification({
          message: `Added ${count} item${count !== 1 ? 's' : ''} to your ${streamLabel}`,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Success',
          type: ToastType.SUCCESS,
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
          title: 'Error',
          type: ToastType.ERROR,
        }),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaFilters,
          QueryKeyConstants.MediaFiltersMediaItems,
        ],
      });
    },
  });

  const mutate = (
    collectionIds: string[],
    options?: Parameters<typeof mutationResult.mutate>[1],
  ) => {
    const cuids = selectedItems.map((item) => item.cuid);
    mutationResult.mutate({ collectionIds, cuids }, options);
  };

  return { ...mutationResult, mutate };
};
